<template>
  <div class="asset-inventory-manage-container">
    <ds-page-header :title="$t('assetInventoryManage.page.title')" :subtitle="$t('assetInventoryManage.page.subtitle')">
      <template #extra>
        <el-button type="primary" size="small" icon="el-icon-plus" @click="_openAddAssetInventoryModal">
          {{ $t('assetInventoryManage.list.inventory') }}
        </el-button>
        <el-button size="small" icon="el-icon-download" @click="_exportAssetInventoryManage">
          {{ $t('common.export') }}
        </el-button>
      </template>
    </ds-page-header>

    <el-row :gutter="16">
      <el-col :xs="24" :sm="6" :md="5" :lg="4">
        <div class="ds-panel ds-panel--padded">
          <div class="ds-panel__header">
            <span class="ds-panel__title">{{ $t('assetInventory.storehouse') }}</span>
          </div>
          <ul class="ds-state-list">
            <li v-for="(item, index) in assetInventoryManageInfo.storehouses" :key="index" @click="swatchStorehouse(item)"
              :class="{ 'is-active': assetInventoryManageInfo.conditions.shId == item.shId }">
              {{ item.shName }}
            </li>
          </ul>
        </div>
      </el-col>
      <el-col :xs="24" :sm="18" :md="19" :lg="20">
        <ds-search-panel>
          <el-row :gutter="16">
            <el-col :xs="24" :sm="12" :md="8" :lg="6">
              <el-input v-model.trim="assetInventoryManageInfo.conditions.staffName" clearable class="w-full"
                :placeholder="$t('assetInventoryManage.search.staffName')" @keyup.enter.native="_queryAssetInventoryMethod" />
            </el-col>
            <el-col :xs="24" :sm="12" :md="8" :lg="6">
              <el-select v-model="assetInventoryManageInfo.conditions.state" clearable class="w-full"
                :placeholder="$t('assetInventoryManage.search.state')">
                <el-option v-for="item in assetInventoryManageInfo.states" :key="item.statusCd" :label="item.name"
                  :value="item.statusCd" />
              </el-select>
            </el-col>
            <el-col :xs="24" :sm="12" :md="8" :lg="6">
              <el-date-picker v-model="assetInventoryManageInfo.conditions.startTime" type="datetime" value-format="yyyy-MM-dd HH:mm:ss"
                :placeholder="$t('assetInventoryManage.search.startTime')" style="width:100%" />
            </el-col>
            <el-col v-show="moreCondition" :xs="24" :sm="12" :md="8" :lg="6">
              <el-date-picker v-model="assetInventoryManageInfo.conditions.endTime" type="datetime" value-format="yyyy-MM-dd HH:mm:ss"
                :placeholder="$t('assetInventoryManage.search.endTime')" style="width:100%" />
            </el-col>
          </el-row>
          <template #actions>
            <el-button type="primary" icon="el-icon-search" @click="_queryAssetInventoryMethod">{{ $t('common.search') }}</el-button>
            <el-button icon="el-icon-refresh-left" @click="_resetAssetInventoryMethod">{{ $t('common.reset') }}</el-button>
            <el-button type="text" @click="moreCondition = !moreCondition">{{ moreCondition ? $t('common.collapse') : $t('common.more') }}</el-button>
          </template>
        </ds-search-panel>

        <div class="ds-panel">
          <ds-toolbar :total="page.total" :density="density" :show-column-setting="false" :show-refresh="true"
            @update:density="density = $event" @refresh="_listAssetInventorys(page.current, page.size)">
            <template #left><span class="ds-panel__title">{{ $t('assetInventoryManage.list.title') }}</span></template>
          </ds-toolbar>
          <ds-data-table :data="assetInventoryManageInfo.assetInventorys" :loading="loading" :error="hasError"
            :error-message="errorMessage" :density="density" border
            :empty-description="$t('assetInventoryManage.page.empty')" @retry="_listAssetInventorys(page.current, page.size)">
            <el-table-column prop="aiId" :label="$t('assetInventoryManage.table.aiId')" align="center" width="150">
              <template slot-scope="scope"><span class="ds-num">{{ scope.row.aiId || '-' }}</span></template>
            </el-table-column>
            <el-table-column prop="name" :label="$t('assetInventoryManage.table.name')" align="center" min-width="120" />
            <el-table-column prop="invTime" :label="$t('assetInventoryManage.table.invTime')" align="center" width="170">
              <template slot-scope="scope"><span class="ds-num">{{ scope.row.invTime || '-' }}</span></template>
            </el-table-column>
            <el-table-column prop="shName" :label="$t('assetInventoryManage.table.shName')" align="center" min-width="110" />
            <el-table-column prop="staffName" :label="$t('assetInventoryManage.table.staffName')" align="center" width="100" />
            <el-table-column prop="stateName" :label="$t('assetInventoryManage.table.state')" align="center" width="110">
              <template slot-scope="scope">
                <ds-status-tag size="sm" :type="scope.row.state == '4000' ? 'success' : scope.row.state == '3000' ? 'danger' : scope.row.state == '2000' ? 'warning' : 'muted'" :label="scope.row.stateName || '-'" />
              </template>
            </el-table-column>
            <el-table-column prop="createTime" :label="$t('assetInventoryManage.table.createTime')" align="center" width="170">
              <template slot-scope="scope"><span class="ds-num">{{ scope.row.createTime || '-' }}</span></template>
            </el-table-column>
            <el-table-column :label="$t('common.operation')" align="center" width="300" fixed="right">
              <template slot-scope="scope">
                <el-button size="mini" @click="_openInStockAssetInventoryModel(scope.row)">{{ $t('common.detail') }}</el-button>
                <el-button v-if="scope.row.state == '1000' && scope.row.staffId == assetInventoryManageInfo.curStaffId"
                  size="mini" type="primary" @click="_openEditAssetInventoryModel(scope.row)">{{ $t('common.edit') }}</el-button>
                <el-button v-if="scope.row.state == '2000' && scope.row.staffId == assetInventoryManageInfo.curStaffId"
                  size="mini" type="warning" @click="_openCancelAssetInventoryModel(scope.row)">{{ $t('assetInventoryManage.list.cancelApply') }}</el-button>
                <el-button v-if="scope.row.state == '2000'" size="mini" type="success"
                  @click="_openAuditAssetInventoryModel(scope.row)">{{ $t('assetInventoryManage.list.audit') }}</el-button>
                <el-button v-if="scope.row.state != '4000' && scope.row.staffId == assetInventoryManageInfo.curStaffId"
                  size="mini" type="danger" @click="_openDeleteAssetInventoryModel(scope.row)">{{ $t('common.delete') }}</el-button>
              </template>
            </el-table-column>
          </ds-data-table>
          <ds-pagination-bar :page="page.current" :size="page.size" :total="page.total"
            @size-change="handleSizeChange" @current-change="handleCurrentChange" />
        </div>
      </el-col>
    </el-row>

    <delete-asset-inventory ref="deleteAssetInventory" @success="handleSuccess" />
    <cancel-asset-inventory ref="cancelAssetInventory" @success="handleSuccess" />
  </div>
</template>

<script>
import { getDict } from '@/api/community/communityApi'
import { getCommunityId } from '@/api/community/communityApi'
import { listAssetInventory, exportAssetInventory, listStorehouses } from '@/api/resource/assetInventoryManageApi'
import DeleteAssetInventory from '@/components/resource/deleteAssetInventory'
import CancelAssetInventory from '@/components/resource/cancelAssetInventory'
import { getUserId } from '@/api/user/userApi'
import DsPageHeader from '@/components/ds/ds-page-header.vue'
import DsSearchPanel from '@/components/ds/ds-search-panel.vue'
import DsToolbar from '@/components/ds/ds-toolbar.vue'
import DsDataTable from '@/components/ds/ds-data-table.vue'
import DsPaginationBar from '@/components/ds/ds-pagination-bar.vue'
import DsStatusTag from '@/components/ds/ds-status-tag.vue'

export default {
  name: 'AssetInventoryManageList',
  components: {
    DeleteAssetInventory,
    CancelAssetInventory,
    DsPageHeader,
    DsSearchPanel,
    DsToolbar,
    DsDataTable,
    DsPaginationBar,
    DsStatusTag
  },
  data() {
    return {
      loading: false,
      hasError: false,
      errorMessage: '',
      density: 'default',
      moreCondition: false,
      assetInventoryManageInfo: {
        assetInventorys: [],
        storehouses: [],
        total: 0,
        records: 1,
        moreCondition: false,
        aiId: '',
        states: [],
        conditions: {
          shId: '',
          staffName: '',
          state: '',
          startTime: '',
          endTime: '',
          communityId: ''
        },
        curStaffId: ''
      },
      page: {
        current: 1,
        size: 10,
        total: 0
      }
    }
  },
  created() {
    this.initData()
  },
  methods: {
    async initData() {
      this.assetInventoryManageInfo.curStaffId = getUserId()
      this.assetInventoryManageInfo.conditions.communityId = await getCommunityId()
      this._listShopHouses()
      this._listAssetInventorys(this.page.current, this.page.size)
      this.getDictData()
    },
    async getDictData() {
      try {
        const data = await getDict('asset_inventory', 'state')
        this.assetInventoryManageInfo.states = data
      } catch (error) {
        console.error('获取字典数据失败:', error)
      }
    },
    async _listAssetInventorys(page, size) {
      try {
        this.loading = true
        this.hasError = false
        const params = {
          page: page,
          row: size,
          ...this.assetInventoryManageInfo.conditions
        }
        const { data, total } = await listAssetInventory(params)
        this.assetInventoryManageInfo.assetInventorys = data
        this.page.total = total
      } catch (error) {
        console.error('获取资产盘点列表失败:', error)
        this.hasError = true
        this.errorMessage = this.$t('common.loadError')
      } finally {
        this.loading = false
      }
    },
    async _listShopHouses() {
      try {
        const params = {
          page: 1,
          row: 100,
          communityId: this.assetInventoryManageInfo.conditions.communityId
        }
        const { data } = await listStorehouses(params)
        this.assetInventoryManageInfo.storehouses = [
          { shName: this.$t('common.all'), shId: '' },
          ...data
        ]
      } catch (error) {
        console.error('获取仓库列表失败:', error)
      }
    },
    _openAddAssetInventoryModal() {
      this.$router.push('/pages/property/assetInventoryIn')
    },
    _openEditAssetInventoryModel(assetInventory) {
      this.$router.push(`/pages/property/assetInventoryEdit?aiId=${assetInventory.aiId}`)
    },
    _openAuditAssetInventoryModel(assetInventory) {
      this.$router.push(`/pages/property/assetInventoryAudit?aiId=${assetInventory.aiId}`)
    },
    _openInStockAssetInventoryModel(assetInventory) {
      this.$router.push(`/pages/property/assetInventoryInStock?aiId=${assetInventory.aiId}`)
    },
    _openDeleteAssetInventoryModel(assetInventory) {
      this.$refs.deleteAssetInventory.open(assetInventory)
    },
    _openCancelAssetInventoryModel(assetInventory) {
      this.$refs.cancelAssetInventory.open(assetInventory)
    },
    _queryAssetInventoryMethod() {
      this.page.current = 1
      this._listAssetInventorys(this.page.current, this.page.size)
    },
    _resetAssetInventoryMethod() {
      this.assetInventoryManageInfo.conditions = {
        shId: '',
        staffName: '',
        state: '',
        startTime: '',
        endTime: '',
        communityId: this.assetInventoryManageInfo.conditions.communityId
      }
      this._listAssetInventorys(this.page.current, this.page.size)
    },
    async _exportAssetInventoryManage() {
      try {
        const params = {
          ...this.assetInventoryManageInfo.conditions,
          pagePath: 'assetInventoryManage'
        }
        await exportAssetInventory(params)
        this.$message.success(this.$t('common.operationSuccess'))
        this.$router.push('/pages/property/downloadTempFile?tab=下载中心')
      } catch (error) {
        console.error('导出失败:', error)
        this.$message.error(this.$t('common.exportFailed'))
      }
    },
    swatchStorehouse(item) {
      this.assetInventoryManageInfo.conditions.shId = item.shId
      this._listAssetInventorys(this.page.current, this.page.size)
    },
    handleSizeChange(val) {
      this.page.size = val
      this._listAssetInventorys(this.page.current, this.page.size)
    },
    handleCurrentChange(val) {
      this.page.current = val
      this._listAssetInventorys(this.page.current, this.page.size)
    },
    handleSuccess() {
      this._listAssetInventorys(this.page.current, this.page.size)
    }
  }
}
</script>

<style lang="scss" scoped>
.asset-inventory-manage-container {
  padding: var(--ds-spacing-lg);
  background-color: var(--ds-gray-100);
  min-height: calc(100vh - 60px);
}
.ds-panel {
  background: #fff;
  border: 1px solid var(--ds-gray-300);
  border-radius: var(--ds-radius-md);
  box-shadow: var(--ds-shadow-sm);
  overflow: hidden;
  margin-bottom: var(--ds-spacing-lg);
  &--padded { padding: var(--ds-spacing-lg); }
  &__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding-bottom: var(--ds-spacing-md);
    margin-bottom: var(--ds-spacing-md);
    border-bottom: 1px solid var(--ds-gray-200);
  }
  &__title {
    font-size: var(--ds-font-size-lg);
    font-weight: 600;
    color: var(--ds-gray-800);
  }
}
.ds-state-list {
  list-style: none;
  margin: 0;
  padding: 0;
  li {
    padding: var(--ds-spacing-sm) var(--ds-spacing-md);
    font-size: var(--ds-font-size-sm);
    color: var(--ds-gray-600);
    border-radius: var(--ds-radius-sm);
    cursor: pointer;
    text-align: center;
    margin-bottom: var(--ds-spacing-xs);
    &:hover { background: var(--ds-gray-100); color: var(--ds-primary); }
    &.is-active { background: var(--ds-primary-sub); color: var(--ds-primary); font-weight: 600; }
  }
}
.w-full { width: 100%; }
</style>
